import { jsx, jsxs, Fragment as Fragment$1 } from "react/jsx-runtime";
import { getCountryCallingCodeById, qualificationLevelOptions } from "@seek/profile-taxonomies";
import { useTranslations, useLanguage } from "@vocab/react";
import { Dialog, Stack, Text, Actions, Button, Box, TextLinkButton, IconChevron, Inline, Columns, Column, Heading, ButtonIcon, IconEdit, Disclosure, ButtonLink, IconNewWindow, Card, Badge, Divider, Tag, TooltipRenderer, IconAttachment } from "braid-design-system";
import { useCallback, useState, useEffect, useRef, Fragment } from "react";
import { translations } from "./components/CountryCallingCodeDialog/.vocab/index.mjs";
import { CredentialStatus, compatibility, validateUnconfirmedQualification, formatters, validateUnconfirmedRole } from "@seek/ca-myprofile-utils";
import { CertsyVerifiedIndicator, ProfileCardDescription, DefinitionList } from "@seek/profile-base-ui";
import { translations as translations$1 } from "./components/ReadMore/.vocab/index.mjs";
import { translations as translations$2 } from "./components/ProfileItemContent/.vocab/index.mjs";
import { useInView } from "react-intersection-observer";
import { translations as translations$3 } from "./components/Licence/.vocab/index.mjs";
import { translations as translations$4 } from "./components/Qualification/.vocab/index.mjs";
import { translations as translations$5 } from "./components/Reference/.vocab/index.mjs";
import { translations as translations$6 } from "./components/ReferenceDetails/.vocab/index.mjs";
import { translations as translations$7 } from "./components/Role/.vocab/index.mjs";
const formatPhoneNumber = (countryCallingCode, phoneNumber) => `+${countryCallingCode} ${phoneNumber.replace(/\s/g, " ")}`;
const CountryCallingCodeDialog = ({
  countryCallingCode: countryCallingCodeValue,
  phoneNumber,
  onSave,
  onClose,
  open,
  width = "xsmall"
}) => {
  const { t } = useTranslations(translations);
  const onSaveClick = useCallback(
    (event) => {
      onSave(event);
      onClose();
    },
    [onSave, onClose]
  );
  const [countryCallingCode, setCountryCallingCode] = useState("");
  useEffect(() => {
    let shouldSetCountryCallingCode = true;
    getCountryCallingCodeById(countryCallingCodeValue.id).then(
      (callingCode) => {
        if (shouldSetCountryCallingCode) {
          setCountryCallingCode(callingCode);
        }
      }
    );
    return () => {
      shouldSetCountryCallingCode = false;
    };
  }, [countryCallingCodeValue.id]);
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      id: "countryCallingCodeDialog",
      width,
      onClose,
      closeLabel: t("Close"),
      open,
      title: t("Country code now included in phone number"),
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", align: "center", children: [
        /* @__PURE__ */ jsx(Text, { align: "center", children: t("Your phone number will be saved as...", {
          formattedPhoneNumber: formatPhoneNumber(
            countryCallingCode,
            phoneNumber
          )
        }) }),
        /* @__PURE__ */ jsxs(Actions, { children: [
          /* @__PURE__ */ jsx(
            Button,
            {
              tone: "formAccent",
              onClick: onSaveClick,
              data: { automation: "country-calling-code-dialog-save-button" },
              children: t("Save")
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              tone: "formAccent",
              variant: "ghost",
              data: { automation: "country-calling-code-dialog-cancel-button" },
              onClick: onClose,
              children: t("Cancel")
            }
          )
        ] })
      ] })
    }
  );
};
const ReadMore = ({
  children,
  linesToPreview = 2,
  moreLabel,
  lessLabel,
  onMoreButtonVisibilityChange,
  onShowMore,
  showFullTextOnly
}) => {
  const { t } = useTranslations(translations$1);
  const fullTextRef = useRef(null);
  const visibleTextRef = useRef(null);
  const [showFullText, setShowFullText] = useState(
    Boolean(showFullTextOnly) === true
  );
  const [showButton, setShowButton] = useState(false);
  const translatedMoreLabel = moreLabel || t("More");
  const translatedLessLabel = lessLabel || t("Less");
  useEffect(() => {
    var _a;
    const calculateIfTextTooLong = (fullEl, visibleTextEl) => {
      const fullTextHeight = fullEl.scrollHeight;
      const visibleTextHeight = visibleTextEl.scrollHeight;
      const isTooLong = fullTextHeight > visibleTextHeight;
      setShowButton(isTooLong);
      if (onMoreButtonVisibilityChange && isTooLong !== showButton) {
        onMoreButtonVisibilityChange(showButton);
      }
    };
    if (fullTextRef.current && visibleTextRef.current) {
      calculateIfTextTooLong(fullTextRef.current, visibleTextRef.current);
      (_a = document.fonts) == null ? void 0 : _a.ready.then(() => {
        if (fullTextRef.current && visibleTextRef.current) {
          calculateIfTextTooLong(fullTextRef.current, visibleTextRef.current);
        }
      });
    }
  }, [onMoreButtonVisibilityChange, showButton, children]);
  const handleShowMore = useCallback(() => {
    setShowFullText(!showFullText);
    if (onShowMore) {
      onShowMore(!showFullText);
    }
  }, [onShowMore, showFullText]);
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsxs(Box, { position: "relative", children: [
      /* @__PURE__ */ jsx(
        Box,
        {
          ref: fullTextRef,
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
          opacity: 0,
          children: /* @__PURE__ */ jsx(Text, { children })
        }
      ),
      /* @__PURE__ */ jsx(Box, { ref: visibleTextRef, "aria-hidden": true, children: /* @__PURE__ */ jsx(Text, { maxLines: showFullText ? void 0 : linesToPreview, children }) })
    ] }),
    showButton && /* @__PURE__ */ jsx(Box, { marginTop: "small", "aria-hidden": true, children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
      TextLinkButton,
      {
        onClick: handleShowMore,
        hitArea: "large",
        weight: "weak",
        icon: /* @__PURE__ */ jsx(
          IconChevron,
          {
            direction: showFullText ? "up" : "down",
            alignY: "lowercase"
          }
        ),
        iconPosition: "trailing",
        children: showFullText ? translatedLessLabel : translatedMoreLabel
      }
    ) }) })
  ] });
};
ReadMore.displayName = "ReadMore";
const UnconfirmedProfileItemContent = ({
  children,
  trackUnconfirmedImpressionFn,
  type
}) => {
  const { ref, inView } = useInView({
    threshold: 1
    // Fully in view
  });
  useEffect(() => {
    if (inView) {
      trackUnconfirmedImpressionFn == null ? void 0 : trackUnconfirmedImpressionFn();
    }
  }, [inView, trackUnconfirmedImpressionFn]);
  return /* @__PURE__ */ jsx(Box, { "data-automation": `read-${type}-unconfirmed`, ref, children });
};
const ProfileItemContent = ({
  type,
  id,
  title,
  titleLink,
  titleLinkHref,
  titleLinkClick,
  subtitle,
  subtitleLink,
  subtitleLinkClick,
  date,
  dateTone = "secondary",
  longText,
  additionalContent,
  addUnconfirmed,
  rejectUnconfirmed,
  editFn,
  trackUnconfirmedImpressionFn,
  confirmed,
  isValid,
  badges,
  warningText,
  replaceUrlTextWithLinks,
  displayVariant,
  isVerified,
  showVerifiedText = false
}) => {
  const { t } = useTranslations(translations$2);
  const [readMore, setReadMore] = useState({
    readMoreClicked: false,
    readMoreVisible: false
  });
  const addToProfileClick = () => {
    if (addUnconfirmed) {
      const contentRead = readMore.readMoreVisible === false || readMore.readMoreVisible && readMore.readMoreClicked;
      addUnconfirmed(id, contentRead);
    }
  };
  const rejectUnconfirmedClick = () => {
    rejectUnconfirmed == null ? void 0 : rejectUnconfirmed(id);
  };
  const editFnClick = () => {
    editFn == null ? void 0 : editFn(id, confirmed);
  };
  const moreButtonVisibilityChange = (readMoreVisible) => setReadMore({ ...readMore, readMoreVisible });
  const readMoreClick = (readMoreVisible) => setReadMore({
    readMoreClicked: true,
    readMoreVisible
  });
  const itemContents = /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
        (badges == null ? void 0 : badges.length) ? /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: badges }) : null,
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsxs(Columns, { space: "gutter", children: [
            /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", alignY: "bottom", children: [
              /* @__PURE__ */ jsx(Heading, { level: "4", children: /* @__PURE__ */ jsx("span", { style: { overflowWrap: "break-word" }, children: title }) }),
              isVerified ? /* @__PURE__ */ jsx(
                CertsyVerifiedIndicator,
                {
                  idSuffix: id,
                  showVerifiedText
                }
              ) : null
            ] }) }),
            editFn ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Box, { paddingLeft: "gutter", children: /* @__PURE__ */ jsx(
              ButtonIcon,
              {
                id: `edit-${type}-${id}`,
                onClick: editFnClick,
                icon: /* @__PURE__ */ jsx(IconEdit, {}),
                variant: "transparent",
                label: t("Edit title", { title }),
                data: { automation: `edit-${type}-${id}` }
              }
            ) }) }) : null
          ] }),
          subtitle ? /* @__PURE__ */ jsxs(Inline, { space: "xsmall", alignY: "bottom", children: [
            /* @__PURE__ */ jsx(Text, { children: subtitle }),
            subtitleLink && confirmed ? /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(
              TextLinkButton,
              {
                onClick: subtitleLinkClick,
                hitArea: "large",
                weight: "weak",
                children: subtitleLink
              }
            ) }) : null
          ] }) : null
        ] })
      ] }),
      date ? /* @__PURE__ */ jsx(Text, { tone: dateTone, component: "time", children: date }) : null,
      warningText ? /* @__PURE__ */ jsx(Text, { tone: "critical", children: warningText }) : null,
      longText ? /* @__PURE__ */ jsx(
        ReadMore,
        {
          lessLabel: t("Less"),
          moreLabel: t("More"),
          linesToPreview: 2,
          onShowMore: readMoreClick,
          onMoreButtonVisibilityChange: moreButtonVisibilityChange,
          showFullTextOnly: displayVariant === "full-text-no-divider",
          children: typeof longText === "string" ? /* @__PURE__ */ jsx(
            ProfileCardDescription,
            {
              text: longText,
              linksAreKeyboardAccessible: readMore.readMoreVisible,
              replaceUrlTextWithLinks
            }
          ) : longText
        }
      ) : null,
      additionalContent ? /* @__PURE__ */ jsx(
        Disclosure,
        {
          id: "additionalContentDislosure",
          weight: "weak",
          collapseLabel: t("Less"),
          expandLabel: t("More"),
          children: typeof additionalContent === "string" ? /* @__PURE__ */ jsx(
            ProfileCardDescription,
            {
              text: additionalContent,
              linksAreKeyboardAccessible: readMore.readMoreVisible,
              replaceUrlTextWithLinks
            }
          ) : additionalContent
        }
      ) : null
    ] }),
    titleLink ? /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(
      ButtonLink,
      {
        href: titleLinkHref,
        onClick: titleLinkClick,
        target: "_blank",
        rel: "noopener",
        role: "button",
        tone: "formAccent",
        icon: /* @__PURE__ */ jsx(IconNewWindow, {}),
        iconPosition: "trailing",
        children: titleLink
      }
    ) }) : null,
    !confirmed && /* @__PURE__ */ jsxs(Actions, { children: [
      /* @__PURE__ */ jsx(
        Button,
        {
          id,
          tone: "formAccent",
          data: { automation: `add-to-profile-${id}` },
          onClick: isValid ? addToProfileClick : editFnClick,
          children: t("Add to Profile")
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          tone: "formAccent",
          variant: "ghost",
          data: { automation: `reject-${type}-${id}` },
          onClick: rejectUnconfirmedClick,
          children: t("Do not include")
        }
      )
    ] })
  ] }) });
  const profileItemContents = () => {
    switch (displayVariant) {
      case "card": {
        return /* @__PURE__ */ jsx(Card, { children: itemContents });
      }
      case "full-text-no-divider": {
        return /* @__PURE__ */ jsx(Box, { paddingY: "small", children: itemContents });
      }
      default: {
        return itemContents;
      }
    }
  };
  return confirmed ? /* @__PURE__ */ jsx(Box, { "data-automation": `read-${type}`, children: profileItemContents() }) : /* @__PURE__ */ jsx(
    UnconfirmedProfileItemContent,
    {
      trackUnconfirmedImpressionFn,
      type,
      children: profileItemContents()
    }
  );
};
const Licence = ({
  id,
  name,
  issuingOrganisation = "",
  description = "",
  editLicence,
  onVerifyClick,
  onReVerifyClick,
  showCertsyFunctionality = true,
  credential,
  status,
  verificationUrl,
  formattedDate,
  replaceUrlTextWithLinks,
  isVerified: isLicenceVerified = false,
  displayVariant,
  showVerifiedText,
  showExpiryStatus
}) => {
  const { t } = useTranslations(translations$3);
  const isVerifiable = Boolean(verificationUrl) && showCertsyFunctionality;
  const isVerified = isLicenceVerified || Boolean(credential);
  const expiryStatus = credential == null ? void 0 : credential.expiryStatus;
  const isNew = status === CredentialStatus.New;
  const credentialInfo = credential == null ? void 0 : credential.credentialInfo;
  const titleLinkClick = useCallback(() => {
    if (isVerifiable) {
      if (isVerified && onReVerifyClick) {
        onReVerifyClick();
      } else if (onVerifyClick) {
        onVerifyClick();
      }
    }
  }, [isVerifiable, isVerified, onReVerifyClick, onVerifyClick]);
  const hasVerifiedCredentials = isVerified && credentialInfo;
  const verifiedCredentialsAndDescription = hasVerifiedCredentials ? /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(DefinitionList, { data: credentialInfo }),
    Boolean(description) && /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("Your description") }),
      /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
        ProfileCardDescription,
        {
          text: description,
          replaceUrlTextWithLinks
        }
      ) })
    ] })
  ] }) : void 0;
  const getBadges = (isVerifed, isNewInput, expiryStatusInput) => {
    const badges = [];
    if (showExpiryStatus && expiryStatusInput === "EXPIRED") {
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "critical", bleedY: true, children: t("Expired") }, "expiredBadge")
      );
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "neutral", bleedY: true, children: t("Only visible to you") }, "onlyVisibleToYouBadge")
      );
    } else if (showExpiryStatus && expiryStatusInput === "EXPIRING_SOON") {
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "caution", bleedY: true, children: t("Expiring soon") }, "expiringSoonBadge")
      );
    } else if (isVerifed && isNewInput) {
      badges.push(
        /* @__PURE__ */ jsx(Badge, { tone: "positive", bleedY: true, children: t("New") }, "newBadge")
      );
    }
    return badges;
  };
  const licenceProps = {
    id,
    type: "licence",
    title: name.text,
    isVerified,
    ...isVerifiable ? {
      titleLink: isVerified ? t("Re-verify") : t("Verify"),
      titleLinkHref: verificationUrl,
      titleLinkClick
    } : {},
    subtitle: issuingOrganisation,
    date: formattedDate,
    longText: !hasVerifiedCredentials ? description : void 0,
    additionalContent: verifiedCredentialsAndDescription,
    editFn: editLicence,
    confirmed: true,
    isValid: true,
    badges: getBadges(isVerified, isNew, expiryStatus),
    replaceUrlTextWithLinks,
    displayVariant,
    showVerifiedText
  };
  return /* @__PURE__ */ jsx(
    ProfileItemContent,
    {
      ...licenceProps
    }
  );
};
Licence.displayName = "Licence";
const Licences = (props) => {
  const {
    licences,
    displayVariant,
    showVerifiedText,
    showExpiryStatus = false,
    ...restProps
  } = props;
  const showDividers = displayVariant !== "card" && displayVariant !== "full-text-no-divider";
  return /* @__PURE__ */ jsx(Stack, { component: "ul", space: displayVariant === "card" ? "small" : "large", children: licences.map((licence, i) => /* @__PURE__ */ jsxs(Fragment, { children: [
    showDividers && i > 0 ? /* @__PURE__ */ jsx(Divider, {}) : null,
    /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(
      Licence,
      {
        ...licence,
        ...restProps,
        displayVariant,
        showVerifiedText,
        showExpiryStatus
      }
    ) })
  ] }, licence.id)) });
};
const ProfileSkills = ({ skills }) => /* @__PURE__ */ jsx(Box, { "data-automation": "skills-items", children: /* @__PURE__ */ jsx(Inline, { space: "small", component: "ul", children: [...skills].reverse().map((skill, i) => /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(Tag, { children: skill }) }, i)) }) });
const useCompatibleLanguage = () => {
  const { language: vocabLanguage } = useLanguage();
  const language = compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);
  return language;
};
const useGetFormattedCompletion = ({
  completed,
  completionDate
}) => {
  const [formattedCompletion, setFormattedCompletion] = useState("");
  const { locale } = useLanguage();
  const language = useCompatibleLanguage();
  useEffect(() => {
    let shouldSetFormattedCompletion = true;
    formatters.formatEducationCompletion(
      { completed, completionDate },
      language,
      locale
    ).then((result) => {
      if (shouldSetFormattedCompletion) {
        setFormattedCompletion(result);
      }
    });
    return () => {
      shouldSetFormattedCompletion = false;
    };
  }, [completed, completionDate, language, locale]);
  return formattedCompletion;
};
const Qualification = ({
  id,
  level2,
  name: { text: qualificationName, ontologyId: qualificationOntologyId },
  institute: {
    text: instituteName,
    ontologyId: qualificationInstituteOntologyId
  },
  completionDate,
  completed,
  formattedCompletion: formattedCompletionProp,
  highlights = "",
  confirmed = true,
  addUnconfirmedQualification,
  rejectUnconfirmedQualification,
  editQualification,
  trackUnconfirmedQualificationImpression,
  tracking,
  onVerifyClick,
  showCertsyFunctionality = true,
  credential,
  status,
  verificationUrl,
  zone = "anz-1",
  replaceUrlTextWithLinks,
  isVerified: isQualificationVerified = false,
  displayVariant,
  showVerifiedText
}) => {
  const { t } = useTranslations(translations$4);
  const [levelOptions, setLevelOptions] = useState([]);
  const language = useCompatibleLanguage();
  useEffect(() => {
    let shouldSetLevelOptions = true;
    qualificationLevelOptions(void 0, language).then((options) => {
      if (shouldSetLevelOptions) {
        setLevelOptions(options);
      }
    });
    return () => {
      shouldSetLevelOptions = false;
    };
  }, [language]);
  const fallbackFormattedCompletion = useGetFormattedCompletion({
    completed,
    completionDate
  });
  const formattedCompletion = formattedCompletionProp ? formattedCompletionProp : fallbackFormattedCompletion;
  const trackImpressionFunction = () => {
    if (trackUnconfirmedQualificationImpression) {
      trackUnconfirmedQualificationImpression({
        id,
        name: { text: qualificationName, ontologyId: qualificationOntologyId },
        institute: {
          text: instituteName,
          ontologyId: qualificationInstituteOntologyId
        },
        completed,
        completionDate,
        highlights,
        tracking
      });
    }
  };
  const isValid = validateUnconfirmedQualification(
    {
      qualificationName,
      instituteName,
      ontologyId: qualificationOntologyId,
      highlights,
      level2
    },
    zone
  );
  const isVerifiable = Boolean(verificationUrl) && showCertsyFunctionality;
  const isVerified = isQualificationVerified || Boolean(credential);
  const isNew = status === CredentialStatus.New;
  const credentialInfo = credential == null ? void 0 : credential.credentialInfo;
  const titleLinkClick = useCallback(() => {
    if (verificationUrl) {
      onVerifyClick == null ? void 0 : onVerifyClick();
    }
  }, [onVerifyClick, verificationUrl]);
  const hasVerifiedCredentials = isVerified && credentialInfo;
  const verifiedCredentialsAndHighlights = hasVerifiedCredentials ? /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(DefinitionList, { data: credentialInfo }),
    Boolean(highlights) && /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("Your highlights") }),
      /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(
        ProfileCardDescription,
        {
          text: highlights,
          replaceUrlTextWithLinks
        }
      ) })
    ] })
  ] }) : void 0;
  const foundInResumeBadge = /* @__PURE__ */ jsx(Badge, { tone: "promote", bleedY: true, children: t("Found in resumé") }, "1");
  const newBadge = /* @__PURE__ */ jsx(Badge, { tone: "positive", bleedY: true, children: t("New") }, "0");
  const badges = [];
  if (!confirmed) {
    badges.push(foundInResumeBadge);
  }
  if (isVerified && isNew) {
    badges.push(newBadge);
  }
  const getTitle = () => {
    if (level2 && level2 !== "NA") {
      const levelTaxonomyItem = levelOptions.find(
        (taxonomyItem) => taxonomyItem.id === level2
      );
      if (levelTaxonomyItem) {
        return t("qualification level in qualification name", {
          qualificationLevel: levelTaxonomyItem.description,
          qualificationName
        });
      }
    }
    return qualificationName;
  };
  const qualificationProps = {
    id,
    type: "qualification",
    isValid,
    title: getTitle(),
    isVerified,
    ...!isVerified && isVerifiable ? {
      titleLink: t("Verify"),
      titleLinkHref: verificationUrl,
      titleLinkClick
    } : {},
    subtitle: isVerified && credential ? null : instituteName,
    date: isVerified && credential ? null : formattedCompletion,
    longText: !hasVerifiedCredentials ? highlights : void 0,
    additionalContent: verifiedCredentialsAndHighlights,
    confirmed,
    addUnconfirmed: addUnconfirmedQualification,
    rejectUnconfirmed: rejectUnconfirmedQualification,
    editFn: editQualification,
    trackUnconfirmedImpressionFn: trackImpressionFunction,
    badges,
    displayVariant,
    showVerifiedText
  };
  return /* @__PURE__ */ jsx(
    ProfileItemContent,
    {
      ...qualificationProps
    }
  );
};
const Qualifications = ({
  qualifications,
  displayVariant,
  showVerifiedText = false,
  ...restProps
}) => {
  const showDividers = displayVariant !== "card" && displayVariant !== "full-text-no-divider";
  return /* @__PURE__ */ jsx(Stack, { component: "ul", space: displayVariant === "card" ? "small" : "large", children: qualifications.map((qualification, i) => /* @__PURE__ */ jsxs(Fragment, { children: [
    showDividers && i > 0 ? /* @__PURE__ */ jsx(Divider, {}) : null,
    /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(
      Qualification,
      {
        ...qualification,
        ...restProps,
        displayVariant,
        showVerifiedText
      }
    ) })
  ] }, qualification.id)) });
};
const Reference = ({
  id,
  refereeFullName,
  relationshipText,
  editCardFn
}) => {
  const { t } = useTranslations(translations$5);
  return /* @__PURE__ */ jsx(Card, { children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Stack, { space: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsxs(Columns, { space: "gutter", children: [
        /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Heading, { level: "4", children: /* @__PURE__ */ jsx("span", { style: { overflowWrap: "break-word" }, children: refereeFullName }) }) }),
        /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
          ButtonIcon,
          {
            id: `edit-reference-${id}`,
            onClick: editCardFn,
            icon: /* @__PURE__ */ jsx(IconEdit, {}),
            variant: "transparent",
            label: t("Edit reference"),
            data: { automation: `edit-reference-${id}` }
          }
        ) })
      ] }),
      /* @__PURE__ */ jsx(Text, { children: relationshipText })
    ] }) }),
    /* @__PURE__ */ jsxs(Inline, { space: "xsmall", alignY: "center", children: [
      /* @__PURE__ */ jsx(
        TooltipRenderer,
        {
          id: `reference-tooltip-${id}`,
          tooltip: /* @__PURE__ */ jsx(Text, { children: t("Only potential employers...") }),
          children: ({ triggerProps }) => /* @__PURE__ */ jsx(Box, { ...triggerProps, children: /* @__PURE__ */ jsx(IconAttachment, { tone: "secondary" }) })
        }
      ),
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("Completed reference check") })
    ] })
  ] }) }) });
};
const ReferenceChecks = ({
  referenceChecks,
  onEditCard = () => {
  }
}) => /* @__PURE__ */ jsx(Stack, { space: "large", children: referenceChecks.map((referenceCheck) => /* @__PURE__ */ jsx(Stack, { space: "small", children: referenceCheck.references.map((reference, referenceIndex) => /* @__PURE__ */ jsx(
  Reference,
  {
    ...reference,
    id: `${referenceCheck.id}~${referenceIndex}`,
    editCardFn: () => onEditCard(`${referenceCheck.id}~${referenceIndex}`)
  },
  referenceIndex.toString()
)) }, referenceCheck.id)) });
const ReferenceIcon = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "134",
    height: "134",
    viewBox: "0 0 134 134",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: [
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M88.5992 25.0195H39.0794C34.6982 25.0195 31.1465 28.5712 31.1465 32.9525V102.485C31.1465 106.866 34.6982 110.417 39.0794 110.417H88.5992C92.9805 110.417 96.5322 106.866 96.5322 102.485V32.9525C96.5322 28.5712 92.9805 25.0195 88.5992 25.0195Z",
          fill: "white"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M88.5993 19.0703H39.0794C31.4246 19.0703 25.1968 25.298 25.1968 32.9529V102.485C25.1968 110.14 31.4246 116.368 39.0794 116.368H88.5993C96.2542 116.368 102.482 110.14 102.482 102.485V32.9529C102.482 25.298 96.2542 19.0703 88.5993 19.0703ZM96.5322 102.485C96.5322 106.866 92.9805 110.418 88.5993 110.418H39.0794C34.6981 110.418 31.1465 106.866 31.1465 102.485V32.9529C31.1465 28.5716 34.6981 25.0199 39.0794 25.0199H88.5993C92.9805 25.0199 96.5322 28.5716 96.5322 32.9529V102.485Z",
          fill: "#0D3880"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M78.5711 17.5372H72.959C72.959 12.3712 68.7713 8.18359 63.6054 8.18359C58.4394 8.18359 54.2518 12.3712 54.2518 17.5372H48.6396C44.5241 17.5372 41.1567 20.9046 41.1567 25.0201H86.054C86.054 20.9046 82.6866 17.5372 78.5711 17.5372Z",
          fill: "#FDC221"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M42.5538 45.4215H42.5487C42.1465 45.4195 41.7626 45.2559 41.4833 44.9665L38.4953 41.8688C37.9245 41.2777 37.9418 40.3351 38.5329 39.7654C39.124 39.1946 40.0665 39.2118 40.6363 39.8029L42.5609 41.7987L47.2014 37.0526C47.7772 36.4666 48.7177 36.4544 49.3048 37.0292C49.8918 37.6031 49.903 38.5456 49.3281 39.1326L43.6172 44.9736C43.3379 45.26 42.954 45.4215 42.5538 45.4215Z",
          fill: "#EE399E"
        }
      ),
      /* @__PURE__ */ jsx("path", { d: "M87.5767 35.4531H55.0518V38.429H87.5767V35.4531Z", fill: "#DCE5F2" }),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M87.5767 43.5977H55.0518V46.5735H87.5767V43.5977Z",
          fill: "#DCE5F2"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M42.5538 70.4336H42.5487C42.1465 70.4316 41.7626 70.268 41.4833 69.9786L38.4953 66.8819C37.9245 66.2908 37.9418 65.3493 38.5329 64.7785C39.1219 64.2087 40.0655 64.2229 40.6363 64.8161L42.5609 66.8108L47.2014 62.0647C47.7772 61.4776 48.7177 61.4665 49.3048 62.0413C49.8918 62.6152 49.903 63.5577 49.3281 64.1447L43.6172 69.9857C43.3379 70.2721 42.954 70.4336 42.5538 70.4336Z",
          fill: "#EE399E"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M87.5767 60.4648H55.0518V63.4407H87.5767V60.4648Z",
          fill: "#DCE5F2"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M87.5767 68.6094H55.0518V71.5852H87.5767V68.6094Z",
          fill: "#DCE5F2"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M42.5538 96.191H42.5487C42.1465 96.189 41.7626 96.0255 41.4833 95.736L38.4953 92.6393C37.9245 92.0482 37.9418 91.1067 38.5329 90.5359C39.1219 89.9661 40.0655 89.9804 40.6363 90.5735L42.5609 92.5682L47.2014 87.8221C47.7772 87.2361 48.7177 87.2239 49.3048 87.7988C49.8918 88.3726 49.903 89.3151 49.3281 89.9022L43.6172 95.7431C43.3379 96.0295 42.954 96.191 42.5538 96.191Z",
          fill: "#EE399E"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M87.5767 86.2227H55.0518V89.1985H87.5767V86.2227Z",
          fill: "#DCE5F2"
        }
      ),
      /* @__PURE__ */ jsx("path", { d: "M83.9148 94.3672H55.0518V97.343H83.9148V94.3672Z", fill: "#DCE5F2" }),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M120.777 59.0317L119.859 58.1131C117.234 55.4883 112.979 55.4883 110.354 58.1131L107.789 60.6777L82.3916 86.075L92.8153 96.4988L118.213 71.1014L118.645 70.6691L120.777 68.5369C123.402 65.9121 123.402 61.6565 120.777 59.0317Z",
          fill: "#EE399E"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M86.1026 97.2236L92.8092 96.4745L82.3853 86.0508L81.6362 92.7573L86.1026 97.2236Z",
          fill: "#F0F6FC"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M81.4247 94.6489C81.245 96.2563 82.6036 97.6149 84.2111 97.4353L86.1022 97.2241L81.6358 92.7578L81.4247 94.6489Z",
          fill: "#0D3880"
        }
      )
    ]
  }
);
const ReferenceField = ({
  label,
  description
}) => /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
  /* @__PURE__ */ jsx(Text, { tone: "secondary", children: label }),
  typeof description === "string" ? /* @__PURE__ */ jsx(Text, { children: description }) : description
] });
const ReferenceDetails = ({
  refereeFullName,
  relationshipText,
  refereeRoleTitle,
  candidateRoleTitle,
  fromDate,
  toDate,
  referenceDate,
  onCompleteClick = () => {
  },
  onManageClick = () => {
  },
  onLearnMoreClick = () => {
  },
  onContinueClick = () => {
  },
  onDisplayCompleteReferenceCheckDialog = () => {
  },
  onDisplayManageOnSeekPassDialog = () => {
  },
  primaryButton
}) => {
  const { t } = useTranslations(translations$6);
  const [
    showCompleteReferenceCheckDialog,
    setShowCompleteReferenceCheckDialog
  ] = useState(false);
  const [showManageOnSeekPassDialog, setShowManageOnSeekPassDialog] = useState(false);
  useEffect(() => {
    if (showCompleteReferenceCheckDialog) {
      onDisplayCompleteReferenceCheckDialog();
    }
  }, [showCompleteReferenceCheckDialog, onDisplayCompleteReferenceCheckDialog]);
  useEffect(() => {
    if (showManageOnSeekPassDialog) {
      onDisplayManageOnSeekPassDialog();
    }
  }, [showManageOnSeekPassDialog, onDisplayManageOnSeekPassDialog]);
  const workedTogetherDescription = /* @__PURE__ */ jsx(Stack, { space: "xsmall", children: /* @__PURE__ */ jsxs(Columns, { space: "xsmall", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("From") }),
      /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t("To") })
    ] }) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Text, { align: "left", children: fromDate.shortAbsoluteLabel }),
      /* @__PURE__ */ jsx(Text, { align: "left", children: (toDate == null ? void 0 : toDate.shortAbsoluteLabel) || t("When reference was provided") })
    ] }) })
  ] }) });
  return /* @__PURE__ */ jsxs(Fragment$1, { children: [
    /* @__PURE__ */ jsx(
      Dialog,
      {
        id: "reference-info-dialog",
        data: { automation: "reference-info-dialog" },
        title: "",
        description: null,
        open: showCompleteReferenceCheckDialog,
        onClose: () => setShowCompleteReferenceCheckDialog(false),
        closeLabel: t("Close"),
        children: /* @__PURE__ */ jsxs(Stack, { space: "large", align: "center", children: [
          /* @__PURE__ */ jsxs(Stack, { space: "small", align: "center", children: [
            /* @__PURE__ */ jsx(ReferenceIcon, {}),
            /* @__PURE__ */ jsx(Heading, { level: "3", children: t("Reference check") })
          ] }),
          /* @__PURE__ */ jsx(Text, { children: t("Reference check involves...") }),
          /* @__PURE__ */ jsx(Text, { children: t("Only potential employers...") }),
          /* @__PURE__ */ jsx(Text, { children: t("To view examples...") }),
          /* @__PURE__ */ jsx(Inline, { space: "small", children: /* @__PURE__ */ jsx(
            ButtonLink,
            {
              href: "https://help.seekpass.co/hc/",
              variant: "transparent",
              target: "_blank",
              tone: "formAccent",
              onClick: onLearnMoreClick,
              children: t("Learn more")
            }
          ) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(
      Dialog,
      {
        id: "seek-pass-dialog",
        data: { automation: "seek-pass-dialog" },
        title: "",
        illustration: /* @__PURE__ */ jsx(Heading, { level: "3", children: t("Manage on SEEK Pass") }),
        description: /* @__PURE__ */ jsx(Text, { children: t("Visit SEEK Pass...") }),
        open: showManageOnSeekPassDialog,
        onClose: () => setShowManageOnSeekPassDialog(false),
        width: "xsmall",
        closeLabel: t("Close"),
        children: /* @__PURE__ */ jsx(Inline, { space: "small", align: "center", children: /* @__PURE__ */ jsx(
          ButtonLink,
          {
            href: "https://app.seekpass.co/career-passport/recommended",
            variant: "solid",
            target: "_blank",
            tone: "formAccent",
            icon: /* @__PURE__ */ jsx(IconNewWindow, {}),
            iconPosition: "trailing",
            onClick: onContinueClick,
            children: t("Continue to SEEK Pass")
          }
        ) })
      }
    ),
    /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
      /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            boxShadow: "borderNeutralLight",
            borderRadius: "large",
            padding: "gutter",
            children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
              /* @__PURE__ */ jsx(Heading, { level: "3", children: refereeFullName }),
              /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
                /* @__PURE__ */ jsx(
                  ReferenceField,
                  {
                    label: t("Working relationship"),
                    description: relationshipText
                  }
                ),
                /* @__PURE__ */ jsx(
                  ReferenceField,
                  {
                    label: t(`Referee's role`),
                    description: refereeRoleTitle
                  }
                ),
                /* @__PURE__ */ jsx(
                  ReferenceField,
                  {
                    label: t("Your role"),
                    description: candidateRoleTitle
                  }
                ),
                /* @__PURE__ */ jsx(
                  ReferenceField,
                  {
                    label: t("Worked together"),
                    description: workedTogetherDescription
                  }
                ),
                /* @__PURE__ */ jsx(
                  ReferenceField,
                  {
                    label: t("Reference provided"),
                    description: referenceDate.shortAbsoluteLabel
                  }
                )
              ] })
            ] })
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            boxShadow: "borderNeutralLight",
            borderRadius: "large",
            padding: "xxsmall",
            children: /* @__PURE__ */ jsxs(Inline, { space: "xsmall", alignY: "center", children: [
              /* @__PURE__ */ jsx(
                Button,
                {
                  icon: /* @__PURE__ */ jsx(IconAttachment, {}),
                  data: { automation: "completed-reference-check-button" },
                  variant: "transparent",
                  onClick: () => {
                    setShowCompleteReferenceCheckDialog(true);
                    onCompleteClick();
                  },
                  children: t("Completed reference check")
                }
              ),
              /* @__PURE__ */ jsx(Badge, { tone: "neutral", children: t("Employer access only") })
            ] })
          }
        )
      ] }),
      /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
        primaryButton ? primaryButton : null,
        /* @__PURE__ */ jsx(
          Button,
          {
            onClick: () => {
              setShowManageOnSeekPassDialog(true);
              onManageClick();
            },
            icon: /* @__PURE__ */ jsx(IconNewWindow, { alignY: "lowercase" }),
            iconPosition: "trailing",
            variant: primaryButton ? "transparent" : "ghost",
            tone: primaryButton ? "formAccent" : "neutral",
            data: { automation: "manage-seek-pass-button" },
            children: t("Manage on SEEK Pass")
          }
        )
      ] })
    ] })
  ] });
};
const useGetFormattedRoleDate = (from, to) => {
  const [formattedRoleDate, setFormattedRoleDate] = useState("");
  const language = useCompatibleLanguage();
  useEffect(() => {
    let shouldSetFormattedRoleDate = true;
    formatters.formatRoleDate({ from, to }, language).then((result) => {
      if (shouldSetFormattedRoleDate) {
        setFormattedRoleDate(result);
      }
    });
    return () => {
      shouldSetFormattedRoleDate = false;
    };
  }, [from, language, to]);
  return formattedRoleDate;
};
const Role = ({
  id,
  company: { text: company, ontologyId: companyOntologyId },
  title: { text: title, ontologyId: titleOntologyId },
  from,
  to: toRaw,
  achievements = "",
  showMissingAchievementsText = false,
  confirmed = true,
  showReviewLink = false,
  onReviewClick,
  addUnconfirmedRole,
  rejectUnconfirmedRole,
  trackUnconfirmedRoleImpression,
  editRole,
  seniority,
  tracking,
  replaceUrlTextWithLinks,
  displayVariant
}) => {
  const { t } = useTranslations(translations$7);
  const to = toRaw && JSON.stringify(toRaw) !== "{}" ? toRaw : void 0;
  const formattedRoleDate = useGetFormattedRoleDate(from, to);
  const trackImpressionFunction = () => {
    if (trackUnconfirmedRoleImpression) {
      trackUnconfirmedRoleImpression({
        id,
        company: { text: company, ontologyId: companyOntologyId },
        title: { text: title, ontologyId: titleOntologyId },
        from,
        to: toRaw,
        achievements,
        seniority,
        tracking
      });
    }
  };
  const isValid = validateUnconfirmedRole({
    title,
    company,
    from,
    to,
    achievements
  });
  const reviewClick = () => {
    const url = `/companies/write-a-review?tracking=overview-personalised&cn=${encodeURIComponent(
      company
    )}`;
    if (onReviewClick) {
      onReviewClick({ id, companyName: company, url });
    } else {
      window.open(url);
    }
  };
  const badges = [
    /* @__PURE__ */ jsx(Badge, { tone: "promote", bleedY: true, children: t("Found in resumé") }, "1")
  ];
  const subtitleLinkProps = showReviewLink ? {
    subtitleLink: t("Review this company"),
    subtitleLinkClick: reviewClick
  } : {};
  const roleProps = {
    id,
    type: "role",
    date: formattedRoleDate,
    isValid,
    subtitle: company,
    title,
    longText: achievements || "",
    reviewClick,
    confirmed,
    addUnconfirmed: addUnconfirmedRole,
    rejectUnconfirmed: rejectUnconfirmedRole,
    editFn: editRole,
    warningText: showMissingAchievementsText ? t("This role has no description.") : void 0,
    trackUnconfirmedImpressionFn: trackImpressionFunction,
    badges: !confirmed ? badges : void 0,
    ...subtitleLinkProps,
    replaceUrlTextWithLinks,
    displayVariant
  };
  return /* @__PURE__ */ jsx(ProfileItemContent, { ...roleProps });
};
const Roles = (props) => {
  const { roles, displayVariant, ...restProps } = props;
  const showDividers = displayVariant !== "card" && displayVariant !== "full-text-no-divider";
  return /* @__PURE__ */ jsx(Stack, { component: "ul", space: displayVariant === "card" ? "small" : "large", children: roles.map((role, i) => /* @__PURE__ */ jsxs(Fragment, { children: [
    showDividers && i > 0 ? /* @__PURE__ */ jsx(Divider, {}) : null,
    /* @__PURE__ */ jsx(Box, { component: "li", children: /* @__PURE__ */ jsx(Role, { ...role, ...restProps, displayVariant }) })
  ] }, role.id)) });
};
export {
  CountryCallingCodeDialog,
  Licence,
  Licences,
  ProfileItemContent,
  ProfileSkills,
  Qualification,
  Qualifications,
  ReadMore,
  Reference,
  ReferenceChecks,
  ReferenceDetails,
  Role,
  Roles
};
